import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Calendar, Tag } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag") || "";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = activeTag ? `?tag=${encodeURIComponent(activeTag)}` : "";
        const { data } = await api.get(`/blog${q}`);
        setPosts(data);
      } catch (e) {
        setPosts([]);
      } finally { setLoading(false); }
    })();
  }, [activeTag]);  const allTags = Array.from(new Set(posts.flatMap((p) => p.tags || [])));

  return (
    <div className="container-x py-12 lg:py-16" data-testid="blog-page">
      <div className="max-w-2xl">
        <span className="tag-pill">Journal</span>
        <h1 className="mt-3 font-outfit text-4xl lg:text-5xl font-medium text-ink tracking-tight">
          The Hysteregistry Journal
        </h1>
        <p className="mt-4 font-manrope text-ink-soft leading-relaxed">
          Guides, stories, and practical wisdom for hysterectomy recovery — from the people living it.
        </p>
      </div>

      {activeTag && (
        <div className="mt-6 flex items-center gap-3" data-testid="blog-active-tag">
          <span className="tag-pill">#{activeTag}</span>
          <button onClick={() => setSearchParams({})} className="text-sm text-brand font-medium hover:underline">
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <p className="mt-10 font-manrope text-ink-soft animate-soft-pulse">Loading posts…</p>
      ) : posts.length === 0 ? (
        <div className="mt-10 card-soft p-10 text-center" data-testid="blog-empty">
          <p className="font-outfit text-lg text-ink">No posts yet.</p>
          <p className="font-manrope text-sm text-ink-soft mt-2">Check back soon.</p>
        </div>
      ) : (
        <>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, idx) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.id}
                className={`card-soft card-soft-hover p-0 overflow-hidden flex flex-col ${idx === 0 && posts.length > 1 ? "md:col-span-2" : ""}`}
                data-testid={`blog-card-${p.slug}`}
              >
                {p.cover_image_url && (
                  <div className="aspect-[2/1] bg-cream-surface overflow-hidden">
                    <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(p.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="tag-pill text-xs">#{t}</span>
                    ))}
                  </div>
                  <h2 className={`mt-3 font-outfit font-medium text-ink leading-tight ${idx === 0 && posts.length > 1 ? "text-2xl lg:text-3xl" : "text-xl"}`}>
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="mt-3 font-manrope text-sm text-ink-soft leading-relaxed line-clamp-3">{p.excerpt}</p>
                  )}
                  <div className="mt-4 flex items-center gap-3 font-manrope text-xs text-ink-soft">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(p.published_at)}</span>
                    {p.author_name && <span>by {p.author_name}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {allTags.length > 0 && (
            <div className="mt-16">
              <h3 className="font-outfit text-sm font-medium text-ink uppercase tracking-wider inline-flex items-center gap-2">
                <Tag className="h-4 w-4" /> Browse by tag
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {allTags.map((t) => (
                  <button
                    key={t}
                    onClick={() => setSearchParams({ tag: t })}
                    className="tag-pill hover:bg-brand hover:text-white transition-colors"
                    data-testid={`blog-tag-${t}`}
                  >
                    #{t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
